import axios from "axios";


const API_URL = "/auth/user/";

class UserAuthService {

    /**
     * Logs in a user with the provided login details.
     *
     * @param {Object} login_details - The login details of the user.
     * @param {string} login_details.username - The username of the user.
     * @param {string} login_details.password - The password of the user.
     * @returns {Promise<Object>} A promise that resolves to an object indicating the success or failure of the login attempt.
     * @returns {Promise<Object>} return.success - Indicates if the login was successful.
     * @returns {Promise<Object>} return.user - The user data if the login was successful.
     * @returns {Promise<Object>} return.error - The error message if the login failed.
     */
    async login(login_details) {
        if (!login_details) return { success: false, error: "Une erreur est survenue" };
        try {
            let res = await axios.post(API_URL + 'login.php', login_details);
            return { success: true, user: res.data };
        }
        catch (err) {
            return { success: false, error: err.response.data.error };
        }
    }

    /**
     * Registers a new user.
     *
     * @param {Object} user - The user object containing registration details.
     * @returns {Promise<Object>} A promise that resolves to an object indicating the success or failure of the registration.
     * @returns {boolean} return.success - Indicates if the registration was successful.
     * @returns {Object} [return.user] - The registered user data if the registration was successful.
     * @returns {string} [return.error] - The error message if the registration failed.
     */
    async register(user) {
        if (!user) return { success: false, error: "Une erreur est survenue" };
        try {
            let res = await axios.post(API_URL + 'register.php', user);
            return { success: true, user: res.data };
        }
        catch (err) {
            return { success: false, error: err.response.data.error };
        }
    }

    /**
     * Logs in a user using an authentication token.
     *
     * @param {string} auth_token - The authentication token.
     * @returns {Promise<Object>} A promise that resolves to an object containing the login result.
     * @returns {boolean} return.success - Indicates whether the login was successful.
     * @returns {Object} [return.user] - The user data if login was successful.
     * @returns {string} [return.error] - The error message if login failed.
     */
    async loginWithAuthToken(auth_token) {
        if (!auth_token) return { success: false, error: "Une erreur est survenue" };
        try {
            let res = await axios.post(API_URL + "auto_login.php", { auth_token: auth_token });
            return { success: true, user: res.data };
        }
        catch (err) {
            return { success: false, error: err.response.data.error };
        }
    }


    /**
     * Sends a password reset email to the specified email address.
     *
     * @param {string} mail - The email address to send the reset email to.
     * @param {string} base_module - The base module information to include in the request.
     * @returns {Promise<Object>} A promise that resolves to an object indicating the success or failure of the operation.
     * @property {boolean} success - Indicates whether the email was sent successfully.
     * @property {string} [message] - A success message if the email was sent successfully.
     * @property {string} [error] - An error message if the email could not be sent.
     */
    async sendResetPassEmail(mail, base_module) {
        if (!mail) return { success: false, error: "Une erreur est survenue" };
        try {
            await axios.post(API_URL + 'reset_password/send.php', { mail: mail, module: { base: base_module } });
            return { success: true, message: "Un email de réinitialisation de mot de passe vous a été envoyé" };
        }
        catch (err) {
            return { success: false, error: err.response.data.error };
        }
    }

    /**
     * Decrypts the reset password token.
     *
     * @param {string} token - The token to be decrypted.
     * @returns {Promise<Object>} A promise that resolves to an object containing the success status and either the decrypted email or an error message.
     */
    async decryptResetPassToken(token) {
        if (!token) return { success: false, error: "Une erreur est survenue" };
        try {
            let res = await axios.post(API_URL + 'reset_password/decrypt_token.php', { token: token });
            return { success: true, mail: res.data };
        }
        catch (err) {
            return { success: false, error: err.response.data.error };
        }
    }


    /**
     * Resets the password for a user.
     *
     * @param {string} token - The token used to authorize the password reset.
     * @param {Object} user - The user object containing user details.
     * @returns {Promise<Object>} A promise that resolves to an object indicating success or failure.
     * @property {boolean} success - Indicates whether the password reset was successful.
     * @property {Object} [user] - The user data returned on success.
     * @property {string} [error] - The error message returned on failure.
     */
    async resetPassword(token, user) {
        if (!token) return { success: false, error: "Une erreur est survenue" };
        if (!user) return { success: false, error: "Une erreur est survenue" };
        try {
            let res = await axios.post(API_URL + 'reset_password/post.php', { token: token, user: user });
            return { success: true, user: res.data };
        }
        catch (err) {
            return { success: false, error: err.response.data.error };
        }
    }

}

export default new UserAuthService();